import {
  buildColumnPathKey,
  columnValueLevelCount,
  idSlot,
  labelSlot,
  type ColumnValue,
  type ColumnValueSegment
} from '@opengov/reporting-types';

/** One node of the nested column header structure. Leaves have no children. */
export interface ColumnHeaderNode {
  /** Escaped path from level 1 down to this node — the node's stable identity. */
  pathKey: string;
  /** Raw ids from level 1 down to this node. */
  ids: ColumnValueSegment[];
  /** This node's own display label, before any rename is applied. Empty when the tuple had none. */
  label: string;
  /** 1-based depth. */
  level: number;
  children: ColumnHeaderNode[];
}

/**
 * A tuple with no level at all still describes one column, so it is read as a single level-1 leaf
 * rather than dropped. The old flat renderer emitted exactly one column per tuple, and losing one
 * silently would be worse than rendering it with a fallback header.
 */
const MINIMUM_LEVELS = 1;

/**
 * The path ids a tuple describes, outermost first — the identity of the leaf column it produces.
 *
 * Callers that need to line a tuple up with the leaf node it became must derive the key through
 * this, so the two cannot disagree about how many levels a tuple carries.
 */
export function columnPathIdsOf(tuple: ColumnValue): ColumnValueSegment[] {
  const levels = Math.max(columnValueLevelCount(tuple), MINIMUM_LEVELS);
  const ids: ColumnValueSegment[] = [];
  for (let level = 1; level <= levels; level++) ids.push(tuple[idSlot(level)]);
  return ids;
}

/** A level's own label, or '' when the tuple carries none, so callers fall back to their own text. */
const labelOf = (tuple: ColumnValue, level: number): string => String(tuple[labelSlot(level)] ?? '');

/**
 * Node identity for the merge map. Scoped by level because a one-segment path is encoded raw, and
 * by whether the node is where a tuple ENDS, because a node that is one tuple's leaf column and
 * another tuple's parent group is not the same node.
 *
 * Without the terminal flag two different columns silently became one. The Totals sentinel encodes
 * to the path key `'null'`, and so does a data column whose level-1 id is null, so whichever arrived
 * second was absorbed into the first and one of them lost its value column entirely — either the
 * totals node grew children and rendered as a group header, or the totals tuple found the deep
 * tuple's parent and emitted no leaf at all. The same collision hit any two tuples of one column
 * group that disagree about depth: the shallower one's leaf is the deeper one's parent.
 */
const nodeKey = (level: number, pathKey: string, isTerminal: boolean): string =>
  `${String(level)}:${isTerminal ? 'leaf' : 'group'}:${pathKey}`;

/**
 * Merges the flat column-values tuples into a nested header tree.
 *
 * Each tuple describes one full path, so tuples sharing a prefix collapse onto shared parent nodes
 * instead of each producing its own chain — which is the whole reason this exists rather than
 * recursing down a single tuple. Insertion order is preserved at every level, because the service
 * already returns rows in the order the headers should render.
 *
 * Pass the tuples of ONE column group. Path keys are scoped to a column group (slot 0 is not part of
 * the path), so tuples from two column groups that share a level-1 id would merge into one node.
 */
export function buildColumnHeaderTree(tuples: ReadonlyArray<ColumnValue>): ColumnHeaderNode[] {
  const roots: ColumnHeaderNode[] = [];
  const byLevelAndPathKey = new Map<string, ColumnHeaderNode>();

  tuples.forEach((tuple) => {
    const pathIds = columnPathIdsOf(tuple);
    const ids: ColumnValueSegment[] = [];
    let siblings = roots;

    for (let level = 1; level <= pathIds.length; level++) {
      ids.push(pathIds[level - 1]);
      const pathKey = buildColumnPathKey(ids);
      const isTerminal = level === pathIds.length;
      const key = nodeKey(level, pathKey, isTerminal);
      const existing = byLevelAndPathKey.get(key);
      if (existing) {
        siblings = existing.children;
        continue;
      }
      const node: ColumnHeaderNode = {
        pathKey,
        ids: [...ids],
        label: labelOf(tuple, level),
        level,
        children: []
      };
      byLevelAndPathKey.set(key, node);
      siblings.push(node);
      siblings = node.children;
    }
  });

  return roots;
}
